import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaretRightOutlined, PauseOutlined, LoadingOutlined, ExclamationCircleOutlined, } from '@ant-design/icons';
import classNames from 'classnames';
import { getDirection } from '../../../helpers';
import { formatAudioTime, generateWaveformBars, getSeekFraction } from './helpers';
import useWaveformBarCount from './useWaveformBarCount';
import s from './VoiceNotePlayer.module.scss';
const PLAYBACK_RATES = [1, 1.5, 2];
const VoiceNotePlayer = ({ src, seed, variant = 'incoming', className }) => {
    const audioRef = useRef(null);
    const waveformRef = useRef(null);
    const isScrubbingRef = useRef(false);
    const isRtlRef = useRef(false);
    const pendingSeekRef = useRef(null);
    const durationRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [duration, setDuration] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const barCount = useWaveformBarCount(waveformRef);
    const bars = useMemo(() => generateWaveformBars(seed, barCount), [seed, barCount]);
    const progress = duration ? Math.min(1, currentTime / duration) : 0;
    useEffect(() => {
        if (!isPlaying)
            return;
        let frame;
        const tick = () => {
            if (!isScrubbingRef.current && audioRef.current) {
                setCurrentTime(audioRef.current.currentTime);
            }
            if (durationRef.current === null) {
                maybeSetDuration();
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [isPlaying]);
    // iOS WebView/blobs report NaN/Infinity until metadata loads
    const maybeSetDuration = () => {
        const audio = audioRef.current;
        if (!audio)
            return;
        let total = audio.duration;
        if (!isFinite(total) || total <= 0) {
            const { seekable } = audio;
            if (seekable.length > 0) {
                total = seekable.end(seekable.length - 1);
            }
        }
        if (isFinite(total) && total > 0) {
            durationRef.current = total;
            setDuration(total);
            if (pendingSeekRef.current !== null) {
                const time = pendingSeekRef.current * total;
                pendingSeekRef.current = null;
                audio.currentTime = time;
                setCurrentTime(time);
            }
        }
    };
    const onTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio || isScrubbingRef.current)
            return;
        setCurrentTime(audio.currentTime);
        if (duration === null) {
            maybeSetDuration();
        }
    };
    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio || hasError)
            return;
        if (isPlaying) {
            audio.pause();
            return;
        }
        const sounds = document.getElementsByTagName('audio');
        for (let i = 0; i < sounds.length; i++) {
            if (sounds[i] !== audio) {
                sounds[i].pause();
            }
        }
        setIsBuffering(true);
        audio.playbackRate = playbackRate;
        audio.play().catch((err) => {
            setIsBuffering(false);
            console.error('Audio play failed', err);
        });
    };
    const cyclePlaybackRate = () => {
        const next = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(playbackRate) + 1) % PLAYBACK_RATES.length];
        setPlaybackRate(next);
        if (audioRef.current) {
            audioRef.current.playbackRate = next;
        }
    };
    const applySeek = (clientX) => {
        const audio = audioRef.current;
        const waveform = waveformRef.current;
        if (!audio || !waveform)
            return;
        const fraction = getSeekFraction(clientX, waveform.getBoundingClientRect(), isRtlRef.current);
        if (!duration) {
            pendingSeekRef.current = fraction;
            return;
        }
        audio.currentTime = fraction * duration;
        setCurrentTime(fraction * duration);
    };
    const onPointerDown = (e) => {
        if (hasError)
            return;
        e.stopPropagation();
        e.preventDefault();
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch (err) {
            console.error('Pointer capture failed', err);
        }
        isRtlRef.current = getComputedStyle(e.currentTarget).direction === 'rtl';
        isScrubbingRef.current = true;
        applySeek(e.clientX);
    };
    const onPointerMove = (e) => {
        if (isScrubbingRef.current) {
            applySeek(e.clientX);
        }
    };
    const stopScrubbing = () => {
        isScrubbingRef.current = false;
    };
    const onEnded = () => {
        setIsPlaying(false);
        setIsBuffering(false);
        setCurrentTime(0);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    };
    const onError = () => {
        var _a;
        setHasError(true);
        setIsPlaying(false);
        setIsBuffering(false);
        console.error('Audio playback error', src, (_a = audioRef.current) === null || _a === void 0 ? void 0 : _a.error);
    };
    const speedVisible = !hasError && (isPlaying || currentTime > 0);
    const renderBars = (played) => bars.map((height, i) => (React.createElement("div", { key: i, className: classNames(s.bar, { [s.barPlayed]: played }), style: { height: `${Math.round(height * 26)}px` } })));
    const unplayedPct = (1 - progress) * 100;
    const clipPath = getDirection() === 'rtl' ? `inset(0 0 0 ${unplayedPct}%)` : `inset(0 ${unplayedPct}% 0 0)`;
    return (React.createElement("div", { className: classNames(s.player, { [s.outgoing]: variant === 'outgoing', [s.error]: hasError }, className) },
        React.createElement("audio", { ref: audioRef, src: src, preload: "metadata", onLoadedMetadata: maybeSetDuration, onDurationChange: maybeSetDuration, onTimeUpdate: onTimeUpdate, onPlay: () => setIsPlaying(true), onPlaying: () => setIsBuffering(false), onWaiting: () => setIsBuffering(true), onPause: () => {
                setIsPlaying(false);
                setIsBuffering(false);
            }, onEnded: onEnded, onError: onError }),
        React.createElement("button", { type: "button", className: s.playButton, onClick: togglePlay, disabled: hasError, "aria-label": isPlaying ? 'Pause' : 'Play' }, hasError ? (React.createElement(ExclamationCircleOutlined, null)) : isBuffering ? (React.createElement(LoadingOutlined, { spin: true })) : isPlaying ? (React.createElement(PauseOutlined, null)) : (React.createElement(CaretRightOutlined, null))),
        React.createElement("div", { className: s.body },
            React.createElement("div", { ref: waveformRef, className: s.waveform, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: stopScrubbing, onPointerCancel: stopScrubbing },
                renderBars(false),
                React.createElement("div", { className: s.playedOverlay, style: { clipPath, WebkitClipPath: clipPath } }, renderBars(true))),
            React.createElement("div", { className: s.times },
                React.createElement("span", { className: s.time }, formatAudioTime(currentTime)),
                React.createElement("span", { className: s.time }, formatAudioTime(duration)))),
        React.createElement("button", { type: "button", className: classNames(s.speedButton, { [s.speedVisible]: speedVisible }), onClick: cyclePlaybackRate, tabIndex: speedVisible ? 0 : -1, "aria-label": "Playback speed" },
            playbackRate,
            "\u00D7")));
};
export default VoiceNotePlayer;
